import React, { useState } from 'react';
import { useAutoFix } from '../../context/AutoFixContext';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  ChevronDown,
  ChevronRight,
  Sparkles,
  Zap,
  Terminal,
  Cpu,
  Layers,
  ShieldCheck,
  Wrench,
  Search,
  Check,
} from 'lucide-react';

export const IncidentTimeline: React.FC = () => {
  const { activeIncident, triggerBug, systemStatus } = useAutoFix();
  const [expandedSteps, setExpandedSteps] = useState<Record<string, boolean>>({});

  const toggleStep = (id: string) => {
    setExpandedSteps((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  if (!activeIncident) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Mission Control Live Banner */}
        <div
          style={{
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(6, 182, 212, 0.08) 100%)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: 10,
            padding: '16px 18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                boxShadow: '0 0 16px rgba(16, 185, 129, 0.4)',
              }}
            >
              <Zap size={22} className="animate-pulse" />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-1)' }}>
                  WebMCP Autonomous Sentinel Active
                </span>
                <span className="badge badge-green" style={{ fontSize: 10 }}>
                  SYSTEM NOMINAL
                </span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 2 }}>
                Monitoring <code>window.navigator.modelContext</code> · 0 active crashes · Ready for fault injection
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 16, textAlign: 'right' }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--green)', fontFamily: 'var(--font-mono)' }}>
                &lt; 250ms
              </div>
              <div style={{ fontSize: 9, color: 'var(--text-3)', textTransform: 'uppercase' }}>Target MTTR</div>
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#38bdf8', fontFamily: 'var(--font-mono)' }}>
                100%
              </div>
              <div style={{ fontSize: 9, color: 'var(--text-3)', textTransform: 'uppercase' }}>Zero Downtime</div>
            </div>
          </div>
        </div>

        {/* 3-Step Architecture Flow */}
        <div
          className="card"
          style={{
            padding: 14,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            background: 'rgba(17, 24, 39, 0.6)',
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-3)' }}>
            The WebMCP Autonomous Immune Response
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
            <div
              style={{
                background: 'rgba(0,0,0,0.3)',
                padding: '10px 12px',
                borderRadius: 6,
                border: '1px solid var(--border)',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: 'var(--red)' }}>
                <AlertTriangle size={13} /> 1. Intercept &amp; Freeze
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-2)', lineHeight: 1.4 }}>
                Uncaught React exception caught at boundary. Flight recorder captures callstack &amp; user clicks.
              </div>
            </div>

            <div
              style={{
                background: 'rgba(0,0,0,0.3)',
                padding: '10px 12px',
                borderRadius: 6,
                border: '1px solid var(--border)',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: 'var(--accent)' }}>
                <Search size={13} /> 2. WebMCP Introspect
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-2)', lineHeight: 1.4 }}>
                Agent calls <code>inspectCrashDump()</code> &amp; <code>getStateSnapshot()</code> via typed JSON schema.
              </div>
            </div>

            <div
              style={{
                background: 'rgba(0,0,0,0.3)',
                padding: '10px 12px',
                borderRadius: 6,
                border: '1px solid var(--border)',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: '#38bdf8' }}>
                <CheckCircle2 size={13} /> 3. Live AST Hotpatch
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-2)', lineHeight: 1.4 }}>
                Gemini 3.8 Flash synthesizes verified AST patch, hot-swapped in memory with zero page reload.
              </div>
            </div>
          </div>
        </div>

        {/* Quick Launch Scenarios Grid */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-1)' }}>
              Choose a Production Incident Scenario to Test:
            </span>
            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
              Click any card to trigger live self-healing
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
            {/* Scenario 1 */}
            <button
              onClick={() => triggerBug('bug-1')}
              className="card"
              style={{
                padding: 12,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                textAlign: 'left',
                cursor: 'pointer',
                borderLeft: '3px solid var(--amber)',
                transition: 'all 0.15s ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-1)' }}>
                  Bug #1: Cart NaN Collapse
                </span>
                <span className="badge badge-amber" style={{ fontSize: 9 }}>PROMO ARITHMETIC</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-2)', lineHeight: 1.3 }}>
                Free promo item with $0 price triggers division-by-zero in cart recalculation, setting total to NaN.
              </div>
              <div style={{ fontSize: 10, color: 'var(--accent)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                <Sparkles size={11} /> Click to Run Scenario ➔
              </div>
            </button>

            {/* Scenario 2 */}
            <button
              onClick={() => triggerBug('bug-2')}
              className="card"
              style={{
                padding: 12,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                textAlign: 'left',
                cursor: 'pointer',
                borderLeft: '3px solid #f472b6',
                transition: 'all 0.15s ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-1)' }}>
                  Bug #2: Discount Stack Flaw
                </span>
                <span className="badge badge-purple" style={{ fontSize: 9 }}>INVOICE UNDERFLOW</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-2)', lineHeight: 1.3 }}>
                Unchecked coupon stacking applies cumulative multipliers, yielding a negative checkout invoice (-$24.50).
              </div>
              <div style={{ fontSize: 10, color: 'var(--accent)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                <Sparkles size={11} /> Click to Run Scenario ➔
              </div>
            </button>

            {/* Scenario 3 */}
            <button
              onClick={() => triggerBug('bug-3')}
              className="card"
              style={{
                padding: 12,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                textAlign: 'left',
                cursor: 'pointer',
                borderLeft: '3px solid var(--red)',
                transition: 'all 0.15s ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-1)' }}>
                  Bug #3: Render TypeError Crash
                </span>
                <span className="badge badge-red" style={{ fontSize: 9 }}>FATAL CRASH</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-2)', lineHeight: 1.3 }}>
                OrderHistory component encounters null API response, calling <code>orders.map()</code> and crashing React render tree.
              </div>
              <div style={{ fontSize: 10, color: 'var(--accent)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                <Sparkles size={11} /> Click to Run Scenario ➔
              </div>
            </button>

            {/* Scenario 4 */}
            <button
              onClick={() => triggerBug('calc-div-zero')}
              className="card"
              style={{
                padding: 12,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                textAlign: 'left',
                cursor: 'pointer',
                borderLeft: '3px solid #38bdf8',
                transition: 'all 0.15s ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-1)' }}>
                  Open Source: Calc 5 ÷ 0
                </span>
                <span className="badge badge-blue" style={{ fontSize: 9 }}>2.7K ⭐ GITHUB REPO</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-2)', lineHeight: 1.3 }}>
                Real-world third-party code: <code>ahfarmer/calculator</code> ZeroDivisionError inside <code>operate.js</code>.
              </div>
              <div style={{ fontSize: 10, color: 'var(--accent)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                <Sparkles size={11} /> Click to Run Scenario ➔
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { bugId, phase, crashDump, diagnosisSteps } = activeIncident;

  const agentPipeline = [
    { name: 'FlightRecorder', role: 'Context Capture', icon: Layers, active: true },
    { name: 'InvestigationAgent', role: 'Root Cause', icon: Search, active: diagnosisSteps.length >= 2 },
    { name: 'PatchSynthesizer', role: 'AST Synthesis', icon: Wrench, active: diagnosisSteps.length >= 4 },
    { name: 'AuditorAgent', role: 'Safety Audit', icon: ShieldCheck, active: diagnosisSteps.length >= 5 },
    { name: 'SandboxVerifier', role: 'Regression Cert', icon: Check, active: phase === 'resolved' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Incident Header Card */}
      <div
        style={{
          background: phase === 'resolved' ? 'rgba(16, 185, 129, 0.08)' : 'rgba(239, 68, 68, 0.08)',
          border: `1px solid ${phase === 'resolved' ? 'var(--green)' : 'var(--red)'}`,
          borderRadius: 'var(--radius-md)',
          padding: 14,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {phase === 'resolved' ? (
              <CheckCircle2 size={18} style={{ color: 'var(--green)' }} />
            ) : (
              <AlertTriangle size={18} style={{ color: 'var(--red)' }} />
            )}
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)' }}>
                Incident: {crashDump.errorType} ({bugId})
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-2)' }}>
                Action: {crashDump.userAction}
              </div>
            </div>
          </div>

          <span
            className={`badge ${phase === 'resolved' ? 'badge-green' : 'badge-red'}`}
            style={{ fontSize: 10 }}
          >
            {phase.toUpperCase()}
          </span>
        </div>

        <div
          style={{
            background: 'rgba(0,0,0,0.4)',
            padding: '8px 10px',
            borderRadius: 'var(--radius-sm)',
            fontSize: 11,
            fontFamily: 'var(--font-mono)',
            color: 'var(--text-1)',
            overflowX: 'auto',
          }}
        >
          {crashDump.errorMessage}
        </div>
      </div>

      {/* Autonomous Multi-Agent Pipeline Matrix */}
      <div
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          padding: '12px 14px',
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            color: 'var(--text-3)',
            marginBottom: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Cpu size={13} style={{ color: 'var(--accent)' }} />
            <span>Autonomous Agent Matrix</span>
          </div>
          <span style={{ fontSize: 10, color: 'var(--accent)', fontFamily: 'var(--font-mono)' }}>
            W3C WebMCP Live
          </span>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: 6,
          }}
        >
          {agentPipeline.map((agent, i) => {
            const Icon = agent.icon;
            const isCurrent = agent.active && (i === agentPipeline.length - 1 || !agentPipeline[i + 1]?.active);

            return (
              <div
                key={agent.name}
                style={{
                  background: agent.active ? 'rgba(16, 185, 129, 0.08)' : 'rgba(15, 23, 42, 0.5)',
                  border: `1px solid ${
                    isCurrent
                      ? 'var(--accent)'
                      : agent.active
                      ? 'rgba(16, 185, 129, 0.3)'
                      : 'var(--border)'
                  }`,
                  borderRadius: 6,
                  padding: '8px 6px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  gap: 4,
                  boxShadow: isCurrent ? '0 0 10px rgba(16,185,129,0.3)' : 'none',
                  transition: 'all 0.2s ease',
                }}
              >
                <Icon
                  size={14}
                  style={{
                    color: agent.active ? 'var(--accent)' : 'var(--text-3)',
                  }}
                />
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: agent.active ? 'var(--text-1)' : 'var(--text-3)',
                    lineHeight: 1.1,
                  }}
                >
                  {agent.name}
                </div>
                <div style={{ fontSize: 8, color: 'var(--text-3)' }}>{agent.role}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Diagnosis Steps Timeline */}
      <div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            color: 'var(--text-3)',
            marginBottom: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <Terminal size={13} style={{ color: 'var(--accent)' }} /> Autonomous Reasoning &amp; WebMCP Tool Execution Timeline
        </div>

        <div className="timeline">
          {diagnosisSteps.map((step, idx) => {
            const isExpanded = !!expandedSteps[step.id];

            return (
              <div
                key={step.id}
                className="timeline-step animate-fade-in"
                style={{ animationDelay: `${idx * 0.04}s` }}
              >
                <div className={`timeline-dot ${step.status}`} />

                <div
                  className="card"
                  style={{
                    padding: '10px 12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                    borderLeft: step.toolName ? '3px solid var(--accent)' : '1px solid var(--border)',
                  }}
                >
                  {/* Step Header */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 8,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span className="badge badge-blue" style={{ fontSize: 9 }}>
                        STEP {idx + 1}: {step.phase.toUpperCase()}
                      </span>

                      {step.toolName && (
                        <span
                          style={{
                            fontSize: 11,
                            fontFamily: 'var(--font-mono)',
                            color: 'var(--accent)',
                            background: 'var(--accent-soft)',
                            padding: '1px 6px',
                            borderRadius: 3,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                          }}
                        >
                          <Terminal size={10} /> navigator.modelContext.{step.toolName}()
                        </span>
                      )}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                      {step.durationMs !== null && (
                        <span
                          style={{
                            fontSize: 10,
                            fontFamily: 'var(--font-mono)',
                            color: 'var(--text-3)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 3,
                          }}
                        >
                          <Clock size={10} /> {step.durationMs}ms
                        </span>
                      )}

                      {Boolean(step.result) && (
                        <button
                          type="button"
                          onClick={() => toggleStep(step.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--text-3)',
                            cursor: 'pointer',
                            padding: 2,
                            display: 'flex',
                            alignItems: 'center',
                          }}
                          title="Toggle result payload"
                        >
                          {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Step Reasoning Thought */}
                  <div style={{ fontSize: 12, color: 'var(--text-1)', lineHeight: 1.4 }}>
                    {step.thought}
                  </div>

                  {/* Collapsible Tool Result Payload */}
                  {isExpanded && Boolean(step.result) && (
                    <div
                      style={{
                        marginTop: 6,
                        background: 'var(--bg-input)',
                        padding: '8px 10px',
                        borderRadius: 4,
                        border: '1px solid var(--border)',
                        fontSize: 11,
                        fontFamily: 'var(--font-mono)',
                        overflowX: 'auto',
                        maxHeight: 180,
                        overflowY: 'auto',
                      }}
                    >
                      <pre style={{ margin: 0, color: 'var(--text-2)' }}>
                        {JSON.stringify(step.result, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Resolution Callout */}
      {phase === 'resolved' && (
        <div
          className="animate-fade-in"
          style={{
            background: 'var(--green-soft)',
            border: '1px solid var(--green)',
            borderRadius: 'var(--radius-md)',
            padding: 14,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: 'var(--glow-green)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <CheckCircle2 size={24} style={{ color: 'var(--green)' }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--green)' }}>
                Application Restored &amp; Regression Verified
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-2)' }}>
                The target app is functioning normally with runtime hotpatch active. Zero downtime, zero page reload.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
